import React, { useEffect, useState, useRef } from 'react'
import {
  Alert,
  BodyText,
  Col,
  Container,
  HeadingText,
  Link,
  Row,
  IconButton,
  ArrowLeftIcon,
  Switch,
  Card,
  EmailIcon,
  ChevronRightIcon,
} from '@paypalcorp/pp-react'
import PrivacySpinner from '../../components/PrivacySpinner'
import { Message } from '@paypalcorp/worldready-react'
import {
  PRIVACY_HOME,
  PYPL_SMARTHELP_HOME_URL,
  SESSION_TIMEOUT,
  SHOPPING_API,
  SHOPPING_NOTIFICATIONS_MARKETING,
} from '../../constants/constants'
import { fptiCustomClick, fptiCustomSetup, serverData } from '../../utils'
import DATAPRIVACY_FPTI from '../../config/data-privacy-fpti'
import { post } from '../../utils/ajax'

const SSError = () => (
  <Row className="pnw_shopping_setting__error">
    <Col>
      <Alert type="error" id="shoppingSettingAlert">
        <Message
          id="paypalShopping.alert.fail"
          link={(innerMessage) => (
            <Link
              inverse
              href={PYPL_SMARTHELP_HOME_URL}
              id="customer_service_link"
              theme="v2"
              target="_blank"
            >
              {innerMessage}
            </Link>
          )}
        />
      </Alert>
    </Col>
  </Row>
)

const ShoppingSetting = ({ onSectionClick }) => {
  const { data, _csrf } = serverData
  const [shoppingData, setShoppingData] = useState({})
  const TURNON = 'TURNON',
    TURNOFF = 'TURNOFF'
  const [isError, setIsError] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const shoppingFPTI = DATAPRIVACY_FPTI.SHOPPING
  const linkRef = useRef()

  const focusLink = () => linkRef.current.focus()
  const navigateTo = (url) => (window.location.href = url)

  useEffect(() => {
    fptiCustomSetup(DATAPRIVACY_FPTI.SHOPPING.IMPRESSION)
    setShoppingData({ id: data.id, optedIn: data.optedIn })
    setIsError(data.isError)
  }, [])

  const postShoppingOptions = async (reqparams, type) => {
    setIsLoading(true)
    try {
      const response = await post(`${SHOPPING_API}/${type}`, reqparams)
      if (response.success) {
        if (response.data.requestUrl) {
          setIsLoading(false)
          setIsError(true)
          setTimeout(function () {
            window.location.href = response.data.requestUrl
          }, SESSION_TIMEOUT)
          return
        }
        setIsError(false)
        const { result } = response.data
        setShoppingData({ id: result.id, optedIn: result.optedIn })
      } else {
        setIsError(true)
      }
    } catch (error) {
      setIsError(true)
    }
    setIsLoading(false)
  }

  const toggleShoppingOptions = (event) => {
    event.preventDefault()
    const type = event.target.checked ? TURNON : TURNOFF
    fptiCustomClick(
      type === TURNON ? shoppingFPTI.TURNON_BTN : shoppingFPTI.TURNOFF_BTN,
    )

    const reqparams = {
      _csrf,
      optedIn: shoppingData.optedIn,
      id: shoppingData.id,
    }

    postShoppingOptions(reqparams, type)
  }

  const ssNav = () => (
    <Row noGutters>
      <Col>
        <IconButton
          id="shoppingSettingMoveBackBtn"
          svgIconComponent={ArrowLeftIcon}
          buttonType="tertiary"
          onClick={() => {
            fptiCustomClick(shoppingFPTI.BACK_NAVIGATION)
            navigateTo(PRIVACY_HOME)
          }}
          data-testid="shoppingSettingMoveBackBtn"
        />
      </Col>
    </Row>
  )

  const ssHeader = () => (
    <>
      <Row className="pnw_shopping_setting__header-title">
        <Col>
          <HeadingText id="shoppingSettingTitle">
            <Message id="paypalShopping.header.new.title1" />
          </HeadingText>
        </Col>
      </Row>
      <Row className="pnw_shopping_setting__header-description">
        <Col>
          <BodyText id="shoppingSettingDescription">
            <Message id="paypalShopping.text.new.description" />
          </BodyText>
        </Col>
      </Row>
    </>
  )

  const ssBody = () => (
    <Row className="pnw_shopping_setting__body">
      <Container form className="pnw_shopping_setting__body-container">
        <Row className="pnw_shopping_setting__body-content">
          <Col>
            <BodyText>
              <Message id="paypalShopping.text.new.body1" />
            </BodyText>
          </Col>
          <Col xs="auto">
            <Switch
              id="shoppingSettingSwitch"
              checked={shoppingData.optedIn}
              hideLabel={true}
              data-testid="shoppingSettingSwitch"
              onChange={toggleShoppingOptions}
              aria-checked={shoppingData.optedIn}
            />
          </Col>
        </Row>
        <Row className="pnw_shopping_setting__body-link">
          <Col>
            <Message
              id="paypalShopping.learnHow.header"
              link={(innerMessage) => (
                <Link
                  id="howItWorksLink"
                  tabIndex="0"
                  ref={linkRef}
                  onClick={(e) => {
                    e.preventDefault()
                    fptiCustomClick(shoppingFPTI.HOW_IT_WORKS)
                    onSectionClick({
                      name: 'shoppingHowItWorks',
                      onSectionClick,
                      focusLink,
                    })
                  }}
                  data-testid="howItWorksLink"
                >
                  {innerMessage}
                </Link>
              )}
            />
          </Col>
        </Row>
      </Container>
    </Row>
  )

  const ssFooter = () => (
    <Row>
      <Col>
        <Card
          id="manageEmailPreference"
          tabIndex="0"
          onClick={() => {
            fptiCustomClick(shoppingFPTI.EMAIL_PREFERENCES)
            navigateTo(SHOPPING_NOTIFICATIONS_MARKETING)
          }}
          data-testid="manageEmailPreference"
          className="pnw_shopping_setting__footer-card"
        >
          <Card.Content>
            <Container form>
              <Row noGutters className="pnw_shopping_setting__footer-content">
                <EmailIcon color="sysColorAccent1Main" />
                <Col>
                  <BodyText>
                    <Message id="paypalShopping.text.new.body2" />
                  </BodyText>
                </Col>
                <ChevronRightIcon color="sysColorAccent1Main" />
              </Row>
            </Container>
          </Card.Content>
        </Card>
      </Col>
    </Row>
  )

  return (
    <Container form>
      {ssNav()}
      <Row noGutters align="center">
        <Container form>
          <Row noGutters align="center">
            <Col md={9}>
              <Container fluid>
                {isError && <SSError />}
                {ssHeader()}
                {ssBody()}
                {ssFooter()}
              </Container>
            </Col>
          </Row>
        </Container>
      </Row>
      {isLoading && <PrivacySpinner />}
    </Container>
  )
}

export default ShoppingSetting
